// lib/access/slidesAccess.ts
//
// Who can use the Slides tool.
//
// Access is a CLOSED allowlist of individual users, and nothing else opens it.
// Not an org, not a user ACTIVITY (those are org-wide grants), and deliberately
// not "any Fater admin": that used to be a blanket bypass here, which meant all
// nine admins of the Fater organisation could read, edit, export and delete
// every deck. The tool is restricted to the people building it and the named URW pilot.
//
// To grant access, add a User.id below (stable; emails change, ids do not) and
// say who it is. To open Slides to everyone with Studio access, delete this
// module and drop the gate back to `canAccessDesign` in Portal — do NOT quietly
// grow this list into a permission system.
//
// There are two lists: the builders, and TRIAL users evaluating the tool. Both
// get in; only the trial users are told, in the UI, that this is a pilot.

import type { UserContext } from '#types/types';

/**
 * TRIAL accounts: outside Fater, here to evaluate the tool rather than build it.
 *
 * Kept separate from the builders because the difference is visible in the UI —
 * a trial user gets the "Limited Trial" pill and the pilot notice on entry, and
 * the people building the tool do not need telling that it has bugs.
 */
const SLIDES_TRIAL_USER_IDS: ReadonlySet<string> = new Set([
  '68fe109090746929fd45570e', // Thibaut AULANIER — thibaut.aulanier@urw.com (URW)
  // RDM pilot, named by Thibaut on the 2026-10-05 call (Steven Lichak deliberately left out).
  '68fe0f2990746929fd45570c', // Louis VIOSSAT — louis.viossat@urw.com
  '68fe0d3c90746929fd45570a', // Alice DASILVA — alice.dasilva@urw.com
  '693c4d48efaff75329e34791', // Eliza Benerat — eliza.benerat@urw.com
  '693bd2227f004859c32e411c', // Fiona Neal — fiona.neal@urw.com
  '6965fe5d8339a7b2406c2022', // Dario Pavon — dario.pavon@urw.com
  '693bd0607f004859c32e411a', // Mario Scotto — mario.scotto@urw.com
  '693bd29c7f004859c32e411d', // Sydney Koster — sydney.koster@urw.com
  'cmtwqfalh01q8qlfbp91fykwe', // Janet Molphy — janet.molphy@urw.com
  'cmnw9y6970003ung2drluj9uk', // Tricia Zeitler — tricia.zeitler@urw.com
  'cmnwa01ep0005ung2u5lzqq9n', // Alfonso Zavala — alfonso.zavala@urw.com
  'cmnw9teui0001ung28fntqvfr', // Jimmy Turner — jimmy.turner@urw.com
]);

/** The people building the tool. */
const SLIDES_BUILDER_USER_IDS: ReadonlySet<string> = new Set([
  '665f5c236de32fb66dad6c81', // Jonathann Jacobs — jonathann@fater.ai
  '680f5aefb1c5d15b63103e4e', // Jonathann Jacobs — jonathannjvp@gmail.com
  '66146f123b47aef3810c55ba', // Youssef Guessous — yg@fater.ai
  '69769fa7169ae03c28659dd6', // Youssef Guessous — youssef@fater.ai
]);

/** The only accounts that may use Slides, by User.id. */
const SLIDES_ALLOWED_USER_IDS: ReadonlySet<string> = new Set([...SLIDES_BUILDER_USER_IDS, ...SLIDES_TRIAL_USER_IDS]);

/** Who may open Training (corrections and the Reviewer doctrine): the two URW review experts
 *  named by the owner on 2026-10-07, and the builders. Other pilot users review decks only. */
const SLIDES_TRAINING_USER_IDS: ReadonlySet<string> = new Set([
  '68fe109090746929fd45570e', // Thibaut AULANIER — thibaut.aulanier@urw.com
  '693bd29c7f004859c32e411d', // Sydney Koster — sydney.koster@urw.com
  ...SLIDES_BUILDER_USER_IDS,
]);

/** The Training gate, for the dashboard switch and every Training route alike. */
export const canAccessSlidesTraining = (user: { dbUserId?: string | null } | null | undefined): boolean => {
  const userId = typeof user?.dbUserId === 'string' ? user.dbUserId.trim() : '';
  return userId.length > 0 && SLIDES_TRAINING_USER_IDS.has(userId);
};

/**
 * The gate, shared by the client and the API routes.
 *
 * `/api/slides/*` is reachable by any authenticated user, so this — not the
 * hidden nav button — is what actually protects a deck. Both halves of the app
 * ask the same question on purpose: the UI check used to additionally require
 * Studio access while the API check did not, so the two could disagree about
 * who was allowed in.
 */
export const isSlidesPilotUser = (
  user: { dbUserId?: string | null } | null | undefined,
): boolean => {
  if (!user) return false;
  const userId = typeof user.dbUserId === 'string' ? user.dbUserId.trim() : '';
  return userId.length > 0 && SLIDES_ALLOWED_USER_IDS.has(userId);
};

/**
 * True for an evaluation account, false for the people building the tool.
 *
 * Drives the "Limited Trial" pill and the pilot notice only — it grants nothing.
 * Access is decided by isSlidesPilotUser above, and a trial id is in that list
 * too, so removing someone from the trial set does NOT revoke their access.
 */
export const isSlidesTrialUser = (
  user: { dbUserId?: string | null } | null | undefined,
): boolean => {
  if (!user) return false;
  const userId = typeof user.dbUserId === 'string' ? user.dbUserId.trim() : '';
  return userId.length > 0 && SLIDES_TRIAL_USER_IDS.has(userId);
};

/** True when the Slides tool should be reachable for this user in the UI. */
export const canAccessSlides = (
  userContext: Pick<UserContext, 'dbUserId'> | null | undefined,
): boolean => isSlidesPilotUser(userContext);
